"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUploadField = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_jss_1 = __importDefault(require("react-jss"));
const react_dropzone_1 = __importDefault(require("react-dropzone"));
const lodash_flowright_1 = __importDefault(require("lodash.flowright"));
const filesize_1 = __importDefault(require("filesize"));
const shortid_1 = __importDefault(require("shortid"));
const utils_1 = require("./utils");
const links_1 = require("../../../../../utils/links");
const formTemplates_1 = require("../../../../formTemplates");
const icons_1 = require("../../../../../../icons");
const file_1 = __importDefault(require("./file"));
const label_1 = __importDefault(require("./label"));
const acceptedFilesDescription_1 = __importDefault(require("./acceptedFilesDescription"));
const useMeasure_1 = __importDefault(require("../../../../../../utils/hooks/useMeasure"));
const withInlineValidation_1 = __importDefault(require("../../../../../wrappers/withInlineValidation"));
const actions_1 = require("./constant/actions");
const errors_1 = require("./constant/errors");
const reducer_1 = require("./reducer");
const fileUpload_style_1 = __importDefault(require("./fileUpload.style"));
const BATCHED_FORM_WIDTH = 375;
const FileUploadField = props => {
    const { classes, schema: { title }, formContext: { uploadFile, uploadConfig, links }, formData, onChange, required, idSchema } = props;
    // store the files list in a ref and update at each rerender
    // this is so that upload function gets bound to the ref and not the list itself
    // so that it can know the real list state at each asynchronous update
    const filesRef = react_1.default.useRef([]);
    filesRef.current = formData || [];
    // check if the widget is locked because of forms links
    const fieldId = idSchema.$id;
    const isLocked = links_1.isLockedWidget(fieldId, links);
    const [dropzoneActive, setDropzoneActive] = react_1.default.useState(false);
    let dropzoneRef = react_1.default.useRef(null);
    // Measures for responsive design
    const container = react_1.default.useRef();
    const [measureRef, bounds] = useMeasure_1.default(container, 0);
    /**
     * Count constants in order to display number of:
     * - files uploaded
     * - error files
     */
    const filesCount = filesRef.current.length;
    const errorsCount = filesRef.current.filter(f => f.error).length;
    // Set to fit within the batched forms
    const responsive = bounds.width <= BATCHED_FORM_WIDTH;
    const maxFileSize = (uploadConfig && uploadConfig.maxFileSize) || 1024 * 1024 * 10;
    // media-api should be feeding this map through the client (e.g: trace-ui)
    const fileExtensionWhitelistMap = (uploadConfig && uploadConfig.fileExtensionWhitelistMap) || null;
    /**
     * @function handleNewFiles - handles the files that are dropped
     * or selected using react-dropzone
     * @param {Array.<Object>} acceptedFiles
     */
    const handleNewFiles = react_1.default.useCallback(acceptedFiles => {
        if (isLocked)
            return;
        // Generate a flat list in order to check for errors on the client side
        const fileExtensionWhitelist = (uploadConfig &&
            uploadConfig.fileExtensionWhitelistMap &&
            Object.values(uploadConfig.fileExtensionWhitelistMap).flat(1)) ||
            [];
        const newFiles = acceptedFiles.filter(f => !filesRef.current.find(i => f.name === i.name));
        onDragLeave();
        if (!newFiles.length)
            return;
        const forbiddenFileExtensions = acceptedFiles.filter(file => fileExtensionWhitelist.length > 0 &&
            !fileExtensionWhitelist.includes(utils_1.extension(file.name)));
        // If a file is over the size limit and its extension is not allowed,
        // we only include this file once in the forbiddenFileExtension array
        const exceedSizeLimit = acceptedFiles.filter(file => file.size > maxFileSize && !forbiddenFileExtensions.includes(file));
        const readyToUpload = acceptedFiles.filter(el => !exceedSizeLimit.includes(el) && !forbiddenFileExtensions.includes(el));
        /**
         * @function onFileSuccess - reports a file with no error and set uploading to false
         * @param id
         */
        const onFileSuccess = (id) => (file) => reducer_1.applyFilesChange(filesRef.current, {
            type: actions_1.SUCCESS,
            payload: { id, file }
        }, onChange);
        /**
         * @function onFileError - reports media-api error.
         * @param id
         * Fallback if the UI is unable to catch the error.
         * e.g: the file size after encryption exceeds media-api's size limit
         */
        const onFileError = (id) => () => reducer_1.applyFilesChange(filesRef.current, { type: actions_1.ERROR, payload: { id } }, onChange);
        /**
         * @function onFileProgress - reports the percentage of the uploading file
         * @param id
         * @param percentage
         */
        const onFileProgress = (id, percentage) => reducer_1.applyFilesChange(filesRef.current, {
            type: actions_1.PROGRESS,
            payload: { id, percentage }
        }, onChange);
        const uploading = readyToUpload.map(file => {
            const id = `valid-${shortid_1.default.generate()}`;
            uploadFile(file, onFileSuccess(id), onFileError(id), progress => onFileProgress(id, progress.uploadPercent));
            return {
                id,
                name: file.name,
                size: file.size,
                uploading: true,
                uploadPercent: 0
            };
        });
        const errorsExceedSizeLimit = exceedSizeLimit.map(file => {
            const id = `err-too-big-${shortid_1.default.generate()}`;
            return {
                id,
                name: file.name,
                size: file.size,
                error: true,
                errorType: errors_1.ERROR_TYPE_SIZE,
                uploading: false
            };
        });
        const errorsForbiddenExtension = forbiddenFileExtensions.map(file => {
            const id = `err-wrong-ext-${shortid_1.default.generate()}`;
            return {
                id,
                name: file.name,
                size: file.size,
                error: true,
                errorType: errors_1.ERROR_TYPE_EXTENSION,
                uploading: false
            };
        });
        const errors = [...errorsExceedSizeLimit, ...errorsForbiddenExtension];
        const mergedFiles = [...uploading, ...errors];
        reducer_1.applyFilesChange(filesRef.current, { type: actions_1.ADD_FILES, payload: { mergedFiles } }, onChange);
    }, [isLocked, maxFileSize, onChange, uploadFile, uploadConfig]);
    const handleDeleteFile = react_1.default.useCallback((id) => reducer_1.applyFilesChange(filesRef.current, { type: actions_1.REMOVE_FILE, payload: { id } }, onChange), [onChange]);
    const onDragEnter = () => !isLocked && setDropzoneActive(true);
    const onDragLeave = () => setDropzoneActive(false);
    const handleFileButtonClick = () => dropzoneRef.open();
    const renderUploadedFiles = react_1.default.useMemo(() => {
        if (filesRef.current.length === 0)
            return null;
        return filesRef.current.map(file => (jsx_runtime_1.jsx(file_1.default, { file: file, handleDeleteFile: handleDeleteFile, maxFileSize: maxFileSize, disabled: isLocked }, file.id)));
    }, [handleDeleteFile, maxFileSize, isLocked]);
    return (jsx_runtime_1.jsx(formTemplates_1.FieldTemplate, Object.assign({ id: fieldId, label: title }, props, { children: jsx_runtime_1.jsxs("div", Object.assign({ className: classes.root, "data-drag-enter": dropzoneActive, ref: measureRef }, { children: [jsx_runtime_1.jsxs(react_dropzone_1.default, Object.assign({ className: classes.dropzoneWrapper, "data-is-responsive": responsive, onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDrop: handleNewFiles, inputProps: { required }, "data-drag-enter": dropzoneActive, multiple: true, disableClick: true, ref: e => {
                        dropzoneRef = e;
                    } }, { children: [jsx_runtime_1.jsx(label_1.default, { filesCount: filesCount, errorsCount: errorsCount }, void 0),
                        jsx_runtime_1.jsxs("div", Object.assign({ className: classes.dropzoneBody, "data-is-responsive": responsive }, { children: [jsx_runtime_1.jsx("ul", Object.assign({ className: classes.leftColumn, "data-is-responsive": responsive, "data-is-disabled": isLocked }, { children: renderUploadedFiles }), void 0),
                                !isLocked && (jsx_runtime_1.jsxs("div", Object.assign({ className: classes.rightColumn, "data-is-responsive": responsive }, { children: [jsx_runtime_1.jsxs("div", Object.assign({ className: classes.rightColumnContent, "data-drag-enter": dropzoneActive, "data-has-files": filesCount > 0, "data-cy": "forms-js-dropzone-fileupload" }, { children: [jsx_runtime_1.jsxs("div", Object.assign({ className: classes.dropzoneLabel }, { children: [jsx_runtime_1.jsx("p", { children: "Drop files here" }, void 0),
                                                        jsx_runtime_1.jsx("p", { children: "or" }, void 0)] }), void 0),
                                                jsx_runtime_1.jsx("button", Object.assign({ className: classes.fileSelectBtn, type: "button", onClick: handleFileButtonClick, "data-cy": "forms-js-select-fileupload" }, { children: "Select files" }), void 0),
                                                maxFileSize && (jsx_runtime_1.jsx("span", Object.assign({ className: classes.sizeWarning }, { children: `Maximum file size is ${filesize_1.default(maxFileSize)}` }), void 0))] }), void 0),
                                        jsx_runtime_1.jsx(acceptedFilesDescription_1.default, { fileExtensionWhitelistMap: fileExtensionWhitelistMap, responsive: responsive, filesLength: filesRef.current.length }, void 0)] }), void 0))] }), void 0)] }), void 0),
                jsx_runtime_1.jsx("div", Object.assign({ className: classes.dropzoneIcon, "data-drag-enter": dropzoneActive }, { children: jsx_runtime_1.jsx(icons_1.Icon, { name: "ArrowDown", size: 22 }, void 0) }), void 0)] }), void 0) }), void 0));
};
exports.FileUploadField = FileUploadField;
exports.default = lodash_flowright_1.default(withInlineValidation_1.default, react_jss_1.default(fileUpload_style_1.default))(react_1.default.memo(exports.FileUploadField));
//# sourceMappingURL=index.js.map